import { FC, useEffect, useRef, useState } from 'react';

import { interpolateStepTemplate, toOrdinal } from './template-utils';
import CtaButton from '@components/quiz/shared/CtaButton';
import StickyCtaBar from '@components/quiz/shared/StickyCtaBar';
import { useReadiness } from '@components/quiz/ReadinessProvider';
import { resolveRaceConfig } from '@components/quiz/ReadinessProvider/utils/race-state';
import type { SanityReadinessLeagueSelectBlock } from '@lib/sanity/types';
import type { MiniLeagueSummary } from 'types/readiness';

type LeagueSelectBlockProps = SanityReadinessLeagueSelectBlock & {
  onSelected: () => void;
  /** Fetch failed or the entry has no mini-leagues → league-type fallback (3b). */
  onFallback: () => void;
};

const FALLBACK_DELAY_MS = 1500;

const describeLeague = (
  league: MiniLeagueSummary,
  templates: {
    rowStandingTemplate?: string;
    rowStandingNoSizeTemplate?: string;
    rowGapBehindTemplate?: string;
    rowGapLevelTemplate?: string;
    rowGapLeadingTemplate?: string;
  },
  /** Gaps below this use the level copy, mirroring the race-state model. */
  behindMinGap: number
): string => {
  const values = {
    position: toOrdinal(league.userRank),
    size: league.leagueSize ?? '',
    gap: 0
  };

  const standing =
    league.leagueSize !== null
      ? interpolateStepTemplate(
          templates.rowStandingTemplate ?? '{{position}} of {{size}}',
          values
        )
      : interpolateStepTemplate(
          templates.rowStandingNoSizeTemplate ?? '{{position}}',
          values
        );

  let gapText: string;
  if (league.userRank === 1) {
    const clear = Math.max(
      0,
      league.userPoints - (league.secondPoints ?? league.userPoints)
    );
    gapText = interpolateStepTemplate(
      templates.rowGapLeadingTemplate ?? '{{gap}} pts clear at the top',
      { ...values, gap: clear }
    );
  } else {
    const gap = Math.max(0, league.leaderPoints - league.userPoints);
    gapText = interpolateStepTemplate(
      gap < behindMinGap
        ? templates.rowGapLevelTemplate ?? '{{gap}} pts off the leader'
        : templates.rowGapBehindTemplate ?? '{{gap}} pts behind the leader',
      { ...values, gap }
    );
  }

  return `${standing} · ${gapText}`;
};

/**
 * Step 3a (post-GW1): the user's REAL mini-leagues from the FPL API.
 * Selecting one sets the race state and wakes the sticky race bar. Any
 * failure or an empty league list falls back to the league-type
 * question (3b) — the funnel never dead-ends here.
 */
const LeagueSelectBlock: FC<LeagueSelectBlockProps> = ({
  rowStandingTemplate,
  rowStandingNoSizeTemplate,
  rowGapBehindTemplate,
  rowGapLevelTemplate,
  rowGapLeadingTemplate,
  loadingText,
  errorText,
  ctaText,
  onSelected,
  onFallback
}) => {
  const { state, actions } = useReadiness();
  const [selectedLeagueId, setSelectedLeagueId] = useState<number | null>(
    state.selectedLeague?.leagueId ?? null
  );

  const hasFetchedRef = useRef(false);
  useEffect(() => {
    if (hasFetchedRef.current || state.miniLeagues) return;
    hasFetchedRef.current = true;
    actions.fetchMiniLeagues();
  }, [actions, state.miniLeagues]);

  const isEmpty = state.miniLeagues !== null && state.miniLeagues.length === 0;
  const shouldFallback = Boolean(state.leaguesError) || isEmpty;

  // Show the error copy briefly, then route to the league-type question.
  useEffect(() => {
    if (!shouldFallback) return;
    const timer = setTimeout(onFallback, FALLBACK_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldFallback, onFallback]);

  const selectedLeague = state.miniLeagues?.find(
    (league) => league.leagueId === selectedLeagueId
  );

  const { behindMinGap } = resolveRaceConfig(state.raceConfig);

  const handleNext = () => {
    if (!selectedLeague) return;
    actions.selectLeague(selectedLeague);
    onSelected();
  };

  return (
    <section className="flex flex-col items-center px-6 pb-28 pt-8">
      <div className="w-full max-w-sm space-y-3">
        {state.isLoadingLeagues && (
          <div className="flex flex-col items-center gap-3 py-10">
            <div className="h-7 w-7 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600" />
            <p className="font-body text-sm text-neutral-500">
              {loadingText ?? 'Pulling your leagues from FPL…'}
            </p>
          </div>
        )}

        {shouldFallback && (
          <p
            className="py-10 text-center font-body text-sm text-neutral-600"
            data-cy="readiness-league-select-error"
          >
            {errorText ??
              "We couldn't pull your leagues from FPL right now — tell us which league you're chasing instead."}
          </p>
        )}

        {state.miniLeagues && state.miniLeagues.length > 0 && (
          <div
            role="radiogroup"
            aria-label="Your mini-leagues"
            className="space-y-3"
          >
            {state.miniLeagues.map((league) => {
              const isSelected = league.leagueId === selectedLeagueId;

              return (
                <button
                  key={league.leagueId}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => setSelectedLeagueId(league.leagueId)}
                  data-cy={`readiness-league-${league.leagueId}`}
                  className={`flex w-full items-center gap-3 rounded-xl border px-5 py-4 text-left transition-all ${
                    isSelected
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-neutral-200 bg-white'
                  }`}
                >
                  <span
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      isSelected
                        ? 'border-primary-600 bg-primary-600'
                        : 'border-neutral-300'
                    }`}
                  >
                    {isSelected && (
                      <span className="h-2 w-2 rounded-full bg-white" />
                    )}
                  </span>
                  <span>
                    <span className="block font-heading text-sm font-bold text-neutral-800">
                      {league.name}
                    </span>
                    <span className="block font-body text-xs text-neutral-500">
                      {describeLeague(
                        league,
                        {
                          rowStandingTemplate,
                          rowStandingNoSizeTemplate,
                          rowGapBehindTemplate,
                          rowGapLevelTemplate,
                          rowGapLeadingTemplate
                        },
                        behindMinGap
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <StickyCtaBar>
        <CtaButton
          onClick={handleNext}
          disabled={!selectedLeague}
          dataCy="readiness-league-select-cta"
        >
          {ctaText ?? 'Measure my race'}
        </CtaButton>
      </StickyCtaBar>
    </section>
  );
};

export default LeagueSelectBlock;
